/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useEmailSettingsColumns.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for defining columns for the email settings list.
────────────────────────────────────────────────────────────────*/

import { useMemo } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import type { EmailConfig } from '../types';

const BOOLEAN_BADGE_STYLES = {
  active: {
    true: 'bg-green-100 text-green-800',
    false: 'bg-red-100 text-red-800',
  },
  default: {
    true: 'bg-blue-100 text-blue-800',
    false: 'bg-gray-100 text-gray-800',
  },
} as const;

export function useEmailSettingsColumnRenderers() {
  const { t } = useI18n();

  return useMemo(
    () => ({
      authType: (value: string) => t(`emailSettings.authTypes.${value}`),
      isActive: (value: boolean) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${
            BOOLEAN_BADGE_STYLES.active[value ? 'true' : 'false']
          }`}
        >
          {value ? t('common.yes') : t('common.no')}
        </span>
      ),
      isDefault: (value: boolean) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${
            BOOLEAN_BADGE_STYLES.default[value ? 'true' : 'false']
          }`}
        >
          {value ? t('common.yes') : t('common.no')}
        </span>
      ),
    }),
    [t]
  );
}

